import React, { useState, useEffect } from 'react';
import { getYrkesgruppDisplayName } from '../../../features/kalkyl/utils/kalkylFormatters';

/**
 * Alla yrkesgrupper som ska kunna prissättas
 */
const YRKESGRUPPER = [
  // Admin
  { section: 'Admin', key: 'arbetsledning', defaultPrice: 750, unit: 'h' },
  { section: 'Admin', key: 'framkorning', defaultPrice: 500, unit: 'st' },
  
  // Riv
  { section: 'Riv', key: 'rivning', defaultPrice: 600, unit: 'h' },
  { section: 'Riv', key: 'bystning', defaultPrice: 600, unit: 'h' },
  { section: 'Riv', key: 'grovstad', defaultPrice: 600, unit: 'h' },
  
  // Bygg
  { section: 'Bygg', key: 'byggnation', defaultPrice: 650, unit: 'h' },
  { section: 'Bygg', key: 'snickeri', defaultPrice: 650, unit: 'h' },
  { section: 'Bygg', key: 'fonstermontage', defaultPrice: 650, unit: 'h' },
  { section: 'Bygg', key: 'fonsterdorrmontage', defaultPrice: 650, unit: 'h' },
  { section: 'Bygg', key: 'grundarbete', defaultPrice: 650, unit: 'h' },
  { section: 'Bygg', key: 'stomarbete', defaultPrice: 650, unit: 'h' },
  { section: 'Bygg', key: 'murarbete', defaultPrice: 650, unit: 'h' },
  { section: 'Bygg', key: 'takarbete', defaultPrice: 650, unit: 'h' },
  { section: 'Bygg', key: 'platarbete', defaultPrice: 650, unit: 'h' },
  
  // Måleri
  { section: 'Måleri', key: 'underarbete', defaultPrice: 580, unit: 'h' },
  { section: 'Måleri', key: 'finisharbete', defaultPrice: 580, unit: 'h' },
  
  // Golv
  { section: 'Golv', key: 'nota', defaultPrice: 620, unit: 'h' },
  { section: 'Golv', key: 'slipaLack', defaultPrice: 620, unit: 'h' },
  { section: 'Golv', key: 'slipaHardvax', defaultPrice: 620, unit: 'h' },
  { section: 'Golv', key: 'slipaLuta', defaultPrice: 620, unit: 'h' },
  { section: 'Golv', key: 'slipaGrov', defaultPrice: 620, unit: 'h' },
  { section: 'Golv', key: 'fiskbensparkett', defaultPrice: 620, unit: 'h' },
  { section: 'Golv', key: 'massivParkettFallandeL', defaultPrice: 620, unit: 'h' },
  { section: 'Golv', key: 'mattlaggning', defaultPrice: 620, unit: 'h' },
  
  // Plattsättning
  { section: 'Plattsättning', key: 'tatskikt', defaultPrice: 630, unit: 'h' },
  { section: 'Plattsättning', key: 'flytspackling', defaultPrice: 630, unit: 'h' },
  { section: 'Plattsättning', key: 'plattsattare', defaultPrice: 630, unit: 'h' },
  
  // El
  { section: 'El', key: 'elektriker', defaultPrice: 700, unit: 'h' },
  
  // VVS
  { section: 'VVS', key: 'vvsInstallator', defaultPrice: 720, unit: 'h' },
  { section: 'VVS', key: 'vvsInstallatorEjRot', defaultPrice: 720, unit: 'h' },
  
  // Specialister
  { section: 'Specialister', key: 'gasInstallator', defaultPrice: 800, unit: 'h' },
  { section: 'Specialister', key: 'oppenGrupp1', defaultPrice: 800, unit: 'h' },
  { section: 'Specialister', key: 'oppenGrupp2', defaultPrice: 800, unit: 'h' },
  
  // Specialister (Ej ROT)
  { section: 'Specialister (Ej ROT)', key: 'pumpbil', defaultPrice: 800, unit: 'h' },
  { section: 'Specialister (Ej ROT)', key: 'stallning', defaultPrice: 800, unit: 'h' },
  { section: 'Specialister (Ej ROT)', key: 'kranbil', defaultPrice: 800, unit: 'h' },
  { section: 'Specialister (Ej ROT)', key: 'markanbud', defaultPrice: 800, unit: 'h' },
  { section: 'Specialister (Ej ROT)', key: 'oppenGrupp1EjRot', defaultPrice: 800, unit: 'h' },
  { section: 'Specialister (Ej ROT)', key: 'oppenGrupp2EjRot', defaultPrice: 800, unit: 'h' },
  
  // Försäkring
  { section: 'Försäkring', key: 'forsakring', defaultPrice: 0, unit: 'st' },
  
  // Övrigt
  { section: 'Övrigt', key: 'besiktningsman', defaultPrice: 1000, unit: 'h' },
  { section: 'Övrigt', key: 'finstad', defaultPrice: 600, unit: 'h' }
];

/**
 * Komponent för att hantera yrkesgruppspriser
 */
export const YrkesgruppPricingSection = ({ values, setValues, isCollapsible = true }) => {
  const [isOpen, setIsOpen] = useState(!isCollapsible); // Om inte collapsible, visa alltid
  const [searchTerm, setSearchTerm] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const yrkesgruppPricing = values.yrkesgruppPricing || {};

  // 🔥 Initiera alla yrkesgrupper med default-värden om de saknas
  // Detta säkerställer att ALLA priser sparas till databasen, även om de matchar defaults
  // Fyller även i saknade "enhet"-fält för befintliga priser
  // Körde tidigare bara vid mount. Det var en lucka: useUpdateBuilderForm gör
  // setValues(getInitialValues()) när byggaren laddas, alltså EFTER mount, och
  // de priserna fick då aldrig sina saknade enhet-fält ifyllda.
  //
  // Nu läses nuläget ur prev i stället för ur values, och effekten reagerar på
  // prisobjektet. Returneras prev oförändrat när inget behöver fyllas i byts
  // ingen referens, så det blir ingen omrendering och ingen loop — efter en
  // ifyllning kör effekten om en gång, hittar inget att göra, och stannar.
  useEffect(() => {
    setValues(prev => {
      const currentPricing = prev.yrkesgruppPricing || {};
      let needsUpdate = false;
      const updatedPricing = { ...currentPricing };

      YRKESGRUPPER.forEach(yg => {
        if (!currentPricing[yg.key]) {
          // Helt saknad yrkesgrupp - lägg till med defaults
          updatedPricing[yg.key] = {
            pris: yg.defaultPrice,
            enhet: yg.unit
          };
          needsUpdate = true;
        } else if (currentPricing[yg.key].enhet === undefined) {
          // Yrkesgrupp finns men saknar enhet - fyll i default-enhet
          updatedPricing[yg.key] = {
            ...currentPricing[yg.key],
            enhet: yg.unit
          };
          needsUpdate = true;
        }
      });

      if (!needsUpdate) return prev;
      return { ...prev, yrkesgruppPricing: updatedPricing };
    });
  }, [values.yrkesgruppPricing, setValues]);

  const handlePriceChange = (key, field, value) => {
    setValues(prev => ({
      ...prev,
      yrkesgruppPricing: {
        ...prev.yrkesgruppPricing,
        [key]: {
          ...(prev.yrkesgruppPricing?.[key] || {}),
          [field]: field === 'pris' ? Number(value) || 0 : value
        }
      }
    }));
  };

  const handleResetToDefaults = () => {
    const defaultPricing = {};
    YRKESGRUPPER.forEach(yg => {
      defaultPricing[yg.key] = {
        pris: yg.defaultPrice,
        enhet: yg.unit
      };
    });
    setValues(prev => ({
      ...prev,
      yrkesgruppPricing: defaultPricing
    }));
    setShowResetConfirm(false);
  };

  // Gruppera per sektion
  const groupedYrkesgrupper = YRKESGRUPPER.reduce((acc, yg) => {
    if (!acc[yg.section]) {
      acc[yg.section] = [];
    }
    acc[yg.section].push(yg);
    return acc;
  }, {});

  // Filtrera baserat på sökning
  const filteredSections = Object.entries(groupedYrkesgrupper).reduce((acc, [section, items]) => {
    const filtered = items.filter(yg => 
      getYrkesgruppDisplayName(yg.key).toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[section] = filtered;
    }
    return acc;
  }, {});

  return (
    <div className={isCollapsible ? "border-t pt-4 mt-6" : ""}>
      {isCollapsible && (
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 text-lg font-medium text-gray-900 hover:text-primary transition-colors"
          >
            <svg
              className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span>Priser per yrkesgrupp</span>
            <span className="text-sm text-gray-500 font-normal">
              (För kalkyl-systemet)
            </span>
          </button>
          
          {isOpen && (
            <button
              type="button"
              onClick={() => setShowResetConfirm(true)}
              className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
            >
              Återställ till standard
            </button>
          )}

          {/* Bekräftelse-modal för återställning */}
          {showResetConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
              <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Bekräfta återställning
                </h3>
                <p className="text-gray-600 mb-6">
                  Är du säker på att du vill återställa alla priser till standardvärden? Alla manuella ändringar går förlorade.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowResetConfirm(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Avbryt
                  </button>
                  <button
                    type="button"
                    onClick={handleResetToDefaults}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Återställ
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {(!isCollapsible || isOpen) && (
        <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
          {/* Info text */}
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="font-medium text-blue-900 mb-1">ℹ️ Om yrkesgruppspriser</p>
            <p>
              Dessa priser används i kalkyl-systemet för att beräkna kostnader per yrkesgrupp.
              Varje byggare kan ha sina egna specifika priser.
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Sök yrkesgrupp..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Yrkesgrupper per sektion */}
          <div className="space-y-6 max-h-[600px] overflow-y-auto">
            {Object.entries(filteredSections).map(([section, items]) => (
              <div key={section} className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                  {section}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map(yg => {
                    const currentPrice = yrkesgruppPricing[yg.key]?.pris ?? yg.defaultPrice;
                    const currentUnit = yrkesgruppPricing[yg.key]?.enhet ?? yg.unit;
                    
                    return (
                      <div key={yg.key} className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          {getYrkesgruppDisplayName(yg.key)}
                        </label>
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <input
                              type="number"
                              value={currentPrice}
                              onChange={(e) => handlePriceChange(yg.key, 'pris', e.target.value)}
                              min="0"
                              step="1"
                              className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                              kr
                            </span>
                          </div>
                          <select
                            value={currentUnit}
                            onChange={(e) => handlePriceChange(yg.key, 'enhet', e.target.value)}
                            className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            <option value="h">h</option>
                            <option value="st">st</option>
                            <option value="kvm">kvm</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {Object.keys(filteredSections).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Inga yrkesgrupper matchar sökningen
            </div>
          )}
        </div>
      )}
    </div>
  );
};

